import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { GALTextInput } from "components/library/gal-text-input";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { TooltipWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { ApiKey, ApiProduct, ModifyApiKey } from "data/models";
import { createApiKey } from "data/mutations-common";
import { fetchApiKeys, fetchApiProducts } from "data/queries-common";
import { Form, Formik } from "formik";
import { Gem, Info } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generatePath, useHistory } from "react-router";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";
import { createApiKeyValidationSchema } from "./validation";

interface CreateKeyDialogProps {
  open: boolean;
  handleClose: () => void;
}

export const CreateKeyDialog = (props: CreateKeyDialogProps) => {
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const apiKeysQuery = useQuery({
      queryKey: [QueryKeys.apiKeys, identityId],
      queryFn: () =>
      fetchApiKeys(),
    });
  const apiProductsQuery = useQuery({
      queryKey: [QueryKeys.apiProducts],
      queryFn: () =>
      fetchApiProducts(),
    });
  const history = useHistory();

  const queryClient = useQueryClient();
  const apiKeyCreateMutation = useMutation({
      mutationFn: (values: ModifyApiKey) => createApiKey(values),
      onSuccess: (createdApiKey) => {
          let allApiKeys = [createdApiKey];
          if (apiKeysQuery.isSuccess) {
            allApiKeys = [...apiKeysQuery.data, createdApiKey];
          }
          queryClient.setQueryData([QueryKeys.apiKeys, identityId], allApiKeys);
          props.handleClose();
          history.push(
            generatePath(Routes.developerApiKeysEditKey, {
              keyName: createdApiKey.name,
            }),
            {
              keyName: createdApiKey.name,
            }
          );
        },
    });

  const isLoading = apiKeysQuery.isPending || apiProductsQuery.isPending;
  const isSuccess = apiKeysQuery.isSuccess && apiProductsQuery.isSuccess;

  const isQueryError = apiKeysQuery.isError || apiProductsQuery.isError;
  const isMutationError = apiKeyCreateMutation.isError;
  const { isPermitted, permissionDeniedMessageString } = usePermissions();

  const premiumPermissionKeys = [PermissionsKeys.premiumDataRead];
  const isDisabledPremiumData = (isPremium?: boolean) => {
    if (isPremium === undefined) {
      return false;
    } else {
      return isPremium && !isPermitted(premiumPermissionKeys);
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.handleClose}
      sx={{
        "& .MuiPaper-root": {
          maxWidth: "700px",
          backgroundImage: "none",
        },
      }}
    >
      {isLoading && (
        <DialogContent sx={{ p: 0 }}>
          <Skeleton variant="rectangular" height="100px" width="700px" />
        </DialogContent>
      )}
      {isQueryError && (
        <DialogContent sx={{ p: 0 }}>
          <Alert variant="outlined" severity="error">
            An error occurred while fetching the API details.
          </Alert>
        </DialogContent>
      )}
      {isSuccess && (
        <Formik<ModifyApiKey>
          enableReinitialize={true}
          initialValues={{
            name: "",
            status: "pending",
            products: [],
            notes: "",
            displayName: "",
          }}
          onSubmit={async (values) => {
            apiKeyCreateMutation.mutate(values);
          }}
          validationSchema={createApiKeyValidationSchema}
        >
          {({
            values: { name, displayName, products, notes },
            handleChange,
            handleBlur,
            handleReset,
            isValid,
            dirty,
            resetForm,
            errors,
            touched,
          }) => {
            return (
              <Form>
                <DialogContent sx={{ p: 0 }}>
                  <Box gap={1} display="flex" flexDirection="column">
                    <GALTextInput
                      characterLimit={60}
                      id="name"
                      name="name"
                      label="Name"
                      type="text"
                      margin="normal"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      value={name}
                      variant="outlined"
                      fullWidth
                      size="small"
                      disabled={apiKeyCreateMutation.isPending}
                      helperText={touched.name && errors.name}
                      sx={{
                        padding: "0px",
                        margin: "0px",
                        marginTop: 1,
                      }}
                    />

                    <GALTextInput
                      characterLimit={200}
                      id="notes"
                      name="notes"
                      label="Note"
                      type="text"
                      size="small"
                      multiline
                      value={notes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.notes && Boolean(errors.notes)}
                      helperText={touched.notes && errors.notes}
                      disabled={apiKeyCreateMutation.isPending}
                      variant="outlined"
                      fullWidth
                      sx={{
                        padding: "0px",
                        margin: "0px",
                        marginTop: 1,
                        marginBottom: "1px",
                      }}
                    />
                    <Stack direction="column" marginTop={0}>
                      <InputLabel
                        htmlFor="view-edit-value-created"
                        sx={{ fontSize: "16px" }}
                      >
                        API Products
                      </InputLabel>
                      <FormGroup>
                        <Grid
                          container
                          sx={{
                            paddingLeft: "14px",
                          }}
                        >
                          {apiProductsQuery.data
                            .sort((a: any, b: any) =>
                              a.displayName.toUpperCase() >
                              b.displayName.toUpperCase()
                                ? 1
                                : -1
                            )
                            .map((product: ApiProduct) => {
                              return (
                                <Grid item xs={12} md={6}>
                                  <Stack
                                    direction="row"
                                    spacing={0}
                                    alignItems="center"
                                  >
                                    <FormControlLabel
                                      sx={{
                                        marginRight: 1,
                                      }}
                                      control={
                                        <Checkbox
                                          id="products"
                                          name="products"
                                          onChange={handleChange}
                                          value={product.name}
                                          checked={products.includes(
                                            product.name
                                          )}
                                          disabled={
                                            apiKeyCreateMutation.isPending ||
                                            isDisabledPremiumData(
                                              product.premium &&
                                                product.premium == "full"
                                            )
                                          }
                                        />
                                      }
                                      label={product.displayName}
                                    />
                                    {product.premium &&
                                      product.premium == "full" && (
                                        <TooltipWrapper
                                          style={{
                                            width: "24px",
                                            height: "24px",
                                          }}
                                          title={
                                            isDisabledPremiumData(
                                              (product.premium &&
                                                product.premium == "full") ||
                                                product.premium == "partial"
                                            )
                                              ? permissionDeniedMessageString(
                                                  premiumPermissionKeys
                                                )
                                              : "Premium Data"
                                          }
                                        >
                                          <Gem color="#1876d3" />
                                        </TooltipWrapper>
                                      )}
                                    {product.premium &&
                                      product.premium == "partial" && (
                                        <TooltipWrapper
                                          style={{
                                            width: "24px",
                                            height: "24px",
                                          }}
                                          title={
                                            isDisabledPremiumData(
                                              product.premium &&
                                                product.premium == "partial"
                                            )
                                              ? permissionDeniedMessageString(
                                                  premiumPermissionKeys,
                                                  {
                                                    upgrade_required:
                                                      "Some functionality is limited to Team or higher",
                                                  }
                                                )
                                              : "Premium Data"
                                          }
                                        >
                                          <Info />
                                        </TooltipWrapper>
                                      )}
                                  </Stack>
                                </Grid>
                              );
                            })}
                        </Grid>
                      </FormGroup>
                    </Stack>
                    <Stack direction="column" spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Info />
                        <Typography variant="caption">
                          Products have some functionality that is only
                          accessible when you have a Team Plan or higher
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Gem color="#1876d3" />
                        <Typography variant="caption">
                          Products are only accessible when you have a Team Plan
                          or higher
                        </Typography>
                      </Stack>
                    </Stack>
                    {isMutationError && (
                      <Alert variant="outlined" severity="error">
                        An error occurred while creating the API Key.
                      </Alert>
                    )}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button
                    disabled={apiKeyCreateMutation.isPending}
                    onClick={() => {
                      resetForm();
                      apiKeyCreateMutation.reset();
                      props.handleClose();
                    }}
                    variant="text"
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    variant="contained"
                    loading={apiKeyCreateMutation.isPending}
                    size="small"
                    type="submit"
                    disabled={!isValid || !dirty}
                  >
                    Save
                  </LoadingButton>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      )}
    </Dialog>
  );
};
